<template>
  <div
    class="content-carousel"
    :class="{ 'content-carousel--bottom-navigation': hasBottomNavigation }"
  >
    <div
      class="content-carousel__container"
      :class="{ 'container': container, 'grid': grid }"
    >
      <div
        v-if="showContentHeader"
        class="content-carousel__header"
        :class="[headerClass, textAlign]"
      >
        <p
          v-if="title"
          class="content-carousel__title text-heading-l"
          v-text="title"
        />

        <p
          v-if="description"
          class="content-carousel__description text-body-m-regular"
          :class="`text-${textAlign}`"
          v-text="description"
        />

        <btn
          v-if="showCta"
          class="content-carousel__button"
          :label="ctaText"
          :url="ctaUrl"
          modifiers="text"
        />
      </div>

      <splide
        class="content-carousel__carousel"
        :class="[{ 'col xs-span': grid }, containerClass]"
        :has-track="false"
        :options="carouselConfig"
        @splide:moved="handleSplideMoved"
      >
        <splide-track>
          <splide-slide
            v-for="card of cards"
            :key="card.title"
            class="content-carousel__slide"
          >
            <product-card
              :loaded-animation="false"
              :product="card"
              :show-badges="showBadges"
              :has-details-animation="hasDetailsAnimation"
              :show-caviar-description="showCaviarDescription"
              :show-taste-notes="showTasteNotes"
              :hide-additional-data="hideAdditionalData"
              :quickview="quickview"
              :ratio="ratio"
              sizes="(min-width: 1024px) 33vw, 282px"
            />
          </splide-slide>
        </splide-track>

        <div class="content-carousel__arrows splide__arrows">
          <button
            class="splide__arrow splide__arrow--prev"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-arrow-left />
          </button>

          <button
            class="splide__arrow splide__arrow--next"
            type="button"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-arrow-right />
          </button>
        </div>

        <ul class="content-carousel__pagination splide__pagination" />

        <ul class="content-carousel__custom-pagination">
          <li
            class="text-body-s-regular"
            v-text="`${currentIndex + 1}/`"
          />
          <li
            class="text-body-s-regular"
            v-text="cards.length"
          />
        </ul>
      </splide>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Content carousel (content-carousel)
 * -----------------------------------------------------------------------------
 * A carousel component to display cards.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean} [container] - Whether the carousel should be wrapped in a
 * container element.
 * @param {Boolean} [grid] - Whether the carousel should be wrapped in a
 * grid element.
 * @param {String} [title] - Section title.
 * @param {String} [description] - Section description.
 * @param {String} [textAlign] - Section text align.
 * @param {String} [ctaUrl] - URL for the CTA.
 * @param {String} [ctaText] - Text for the CTA.
 * @param {Array} [cards] - Array of cards for the carousel.
 * @param {Boolean} [carouselPagination] - Whether to display carousel
 * pagination.
 * @param {Boolean} [showCaviarDescription] - Shows caviar description.
 * @param {Boolean} [showBadges] - Shows badges.
 * @param {String} [headerClass] - Header class.
 * @param {Boolean} [hasDetailsAnimation] - Shows additional data on hover.
 * @param {String} [ratio] - Media ratio.
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import Btn from '~global/btn/btn'
import ProductCard from '~global/product-card/product-card'

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg'
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ContentCarousel',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
  },

  props: {
    container: {
      type: Boolean,
      default: true,
    },
    grid: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    textAlign: {
      type: String,
      default: 'center',
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaUrl: {
      type: String,
      default: '',
    },
    cards: {
      type: Array,
      default: () => [],
    },
    carouselPagination: {
      type: Boolean,
      default: false,
    },
    showCaviarDescription: {
      type: Boolean,
      default: true,
    },
    showTasteNotes: {
      type: Boolean,
      default: true,
    },
    showBadges: {
      type: Boolean,
      default: true,
    },
    headerClass: {
      type: String,
      default: 'col xs-span l5-8',
    },
    containerClass: {
      type: String,
      default: '',
    },
    hasDetailsAnimation: {
      type: Boolean,
      default: false,
    },
    hideAdditionalData: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: '3up',
    },
    hasBottomNavigation: {
      type: Boolean,
      default: false,
    },
    quickview: {
      type: Boolean,
      default: false,
    },
    ratio: {
      type: String,
      default: '4:5',
    },
  },

  data() {
    return {
      currentIndex: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screenWidth: (state) => state.index.screen.width,
    }),

    /**
     * Calculate Splide config object.
     * - Disables dragging if there are not enough cards.
     * - Arrows and pagination are disabled with CSS in the main Splide
     *   stylesheet.
     * @returns {Object} splide config.
     */
    carouselConfig() {
      const isOverflow = this.screenWidth < breakpoints.m
        ? this.cards.length > 1
        : this.cards.length > 3

      return {
        arrows: true,
        drag: isOverflow,
        gap: '24px',
        mediaQuery: 'min',
        pagination: this.carouselPagination,
        type: 'slide',
        omitEnd: false,
        padding: {
          right: this.mode === 'peek' ? '15%' : 0,
          left: this.mode === 'peek' ? '16px' : 0,
        },
        breakpoints: {
          768: {
            perPage: this.mode === '3up' ? 3 : 2,
            gap: '16px',
            padding: {
              right: this.mode === 'peek' ? '15%' : 0,
              left: 0,
            },
          },
        },
      }
    },

    /**
     * Compute if content header should show.
     * @returns {Boolean}
     */
    showContentHeader() {
      return Boolean(this.title || this.description || this.showCta)
    },

    /**
     * Compute if content header CTA should show.
     * @returns {Boolean}
     */
    showCta() {
      return Boolean(this.ctaText && this.ctaUrl)
    },
  },

  methods: {

    /**
     * Handle Splide moved event.
     * - Updates current slide index.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     */
    handleSplideMoved(_, newIndex) {
      this.currentIndex = newIndex
    },
  },
}
</script>

<style lang="scss">
@import './content-carousel';
</style>
